import { MathUtils, PMREMGenerator, Scene, Vector3, type Texture, type WebGLRenderer } from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';

/** Haze colour at the horizon, used for fog so distant things fade into the sky. */
export const SKY = { haze: '#cdd3d6' } as const;

/** Afternoon sun over Accra: fairly high, a little west. */
export const SUN = { elevationDeg: 42, azimuthDeg: 235 } as const;

export function sunDirection(elevationDeg: number = SUN.elevationDeg): Vector3 {
  const phi = MathUtils.degToRad(90 - elevationDeg);
  const theta = MathUtils.degToRad(SUN.azimuthDeg);
  return new Vector3().setFromSphericalCoords(1, phi, theta);
}

/**
 * Physically based sky (Rayleigh + Mie scattering) with tropical haze, plus an environment map baked
 * from it once, so paint, glass and metal reflect the real sky and shadows are filled with sky light.
 */
export function createSky(renderer: WebGLRenderer): { sky: Sky; environment: Texture } {
  const sky = new Sky();
  sky.scale.setScalar(2000);
  const u = sky.material.uniforms;
  u.turbidity!.value = 8;
  u.rayleigh!.value = 1.1;
  u.mieCoefficient!.value = 0.006;
  u.mieDirectionalG!.value = 0.8;
  u.sunPosition!.value.copy(sunDirection());

  const pmrem = new PMREMGenerator(renderer);
  const envScene = new Scene();
  const envSky = new Sky();
  envSky.scale.setScalar(2000);
  for (const key of Object.keys(u)) envSky.material.uniforms[key]!.value = u[key]!.value;
  envScene.add(envSky);
  const environment = pmrem.fromScene(envScene).texture;
  pmrem.dispose();
  return { sky, environment };
}
